import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Sprout, Menu, X, LogOut } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";

const links = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/volunteer", label: "Volunteer" },
  { to: "/jobs", label: "Jobs" },
  { to: "/food", label: "Food" },
  { to: "/mentorship", label: "Mentorship" },
  { to: "/stats", label: "Impact" },
  { to: "/certificate", label: "Certificates" },
  { to: "/pitch", label: "Pitch" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 glass-nav border-b border-border/60">
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-4">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <div className="h-9 w-9 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shadow-sm group-hover:rotate-6 transition-transform">
            <Sprout size={18} strokeWidth={2} />
          </div>
          <span className="font-display text-xl font-bold tracking-tight text-foreground">
            NeighborNet<span className="text-secondary"> AI</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-user-menu"
                  className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-muted transition-colors"
                >
                  <div className="h-8 w-8 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center font-semibold text-sm">
                    {user.name.charAt(0)}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">{user.name.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-2xl">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/post-need")} data-testid="menu-post-need">
                  Post a Need
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/offer-help")} data-testid="menu-offer-help">
                  Offer Help
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut size={14} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate("/login")}
                data-testid="nav-login-btn"
                className="rounded-full hidden sm:inline-flex"
              >
                Log in
              </Button>
              <Button
                onClick={() => navigate("/signup")}
                data-testid="nav-signup-btn"
                className="rounded-full bg-primary hover:bg-primary/90 shadow-sm hover:-translate-y-0.5 transition-all"
              >
                Join the Net
              </Button>
            </>
          )}
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 rounded-full hover:bg-muted"
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-border/60 bg-background/95 backdrop-blur-xl">
          <div className="px-4 py-3 flex flex-col gap-1">
            {links.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-sm font-medium ${
                    isActive ? "bg-accent text-accent-foreground" : "hover:bg-muted"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
